import { apiRequest } from "../../api/apiClient";
import type {
  CertificateApplication,
  CreateCertificateApplicationDto,
  UpdateCertificateApplicationDto,
  County,
  Nationality,
  CertificateSignature,
  SignedCertificate,
  AuthoritySignature,
} from "../../types/certificate.types";

const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ||
        error.title ||
        `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  // For successful DELETE or status with no body, return empty object
  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return {} as T;
  }

  return response.json();
};

export const certificateService = {
  /**
   * Get all certificate applications
   */
  getCertificateApplications: async (): Promise<CertificateApplication[]> => {
    const response = await apiRequest("/CertificateApplications", {
      method: "GET",
    });
    return handleResponse<CertificateApplication[]>(response);
  },

  /**
   * Get certificate applications by category
   */
  getCertificateApplicationsByCategory: async (
    category: string,
  ): Promise<CertificateApplication[]> => {
    const response = await apiRequest(
      `/CertificateApplications/category/${encodeURIComponent(category)}`,
      {
        method: "GET",
      },
    );
    return handleResponse<CertificateApplication[]>(response);
  },

  /**
   * Get certificate applications assigned to current module's approval task queue
   */
  getCertificateApplicationsByMyTask: async (): Promise<
    CertificateApplication[]
  > => {
    const response = await apiRequest(`/CertificateApplications/my-tasks`, {
      method: "GET",
    });

    return handleResponse<CertificateApplication[]>(response);
  },

  /**
   * Get divorce applications assigned to current user's tasks
   */
  getDivorceApplicationsByMyTasks: async (): Promise<
    CertificateApplication[]
  > => {
    const response = await apiRequest(`/DivorceApplicants/my-tasks`, {
      method: "GET",
    });

    return handleResponse<CertificateApplication[]>(response);
  },

  /**
   * Get certificate applications by status ID
   */
  getCertificateApplicationsByStatus: async (
    statusId: number,
  ): Promise<CertificateApplication[]> => {
    const response = await apiRequest(
      `/CertificateApplications/status/${statusId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<CertificateApplication[]>(response);
  },

  /**
   * Get a single certificate application by ID
   */
  getCertificateApplicationById: async (
    id: number,
  ): Promise<CertificateApplication> => {
    const response = await apiRequest(`/CertificateApplications/${id}`, {
      method: "GET",
    });
    return handleResponse<CertificateApplication>(response);
  },

  /**
   * Create a new certificate application
   */
  createCertificateApplication: async (
    data: CreateCertificateApplicationDto,
  ): Promise<CertificateApplication> => {
    const response = await apiRequest("/CertificateApplications", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<CertificateApplication>(response);
  },

  /**
   * Update an existing certificate application
   */
  updateCertificateApplication: async (
    id: number,
    data: UpdateCertificateApplicationDto,
  ): Promise<CertificateApplication> => {
    const response = await apiRequest(`/CertificateApplications/${id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
    return handleResponse<CertificateApplication>(response);
  },

  /**
   * Delete a certificate application
   */
  deleteCertificateApplication: async (id: number): Promise<void> => {
    const response = await apiRequest(`/CertificateApplications/${id}`, {
      method: "DELETE",
    });
    await handleResponse<void>(response);
  },

  /**
   * Get all counties
   */
  getCounties: async (): Promise<County[]> => {
    const response = await apiRequest("/Counties", {
      method: "GET",
    });
    return handleResponse<County[]>(response);
  },

  /**
   * Get all nationalities
   */
  getNationalities: async (): Promise<Nationality[]> => {
    const response = await apiRequest("/Nationalities", {
      method: "GET",
    });
    return handleResponse<Nationality[]>(response);
  },

  /**
   * Check if certificate application is ready for submission
   */
  checkReadyForSubmission: async (
    id: number,
    moduleId: number,
  ): Promise<Record<string, boolean>> => {
    const response = await apiRequest(
      `/CertificateApplications/${id}/ready-for-submission/${moduleId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<Record<string, boolean>>(response);
  },

  /**
   * Change certificate application status
   */
  changeStatus: async (
    id: number,
    statusId: number,
  ): Promise<CertificateApplication> => {
    const response = await apiRequest(
      `/CertificateApplications/${id}/change-status`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ statusId }),
      },
    );
    return handleResponse<CertificateApplication>(response);
  },

  /**
   * Get signed certificate by application ID
   */
  getSignedCertificateByApplicationId: async (
    applicationId: number,
  ): Promise<SignedCertificate | null> => {
    const response = await apiRequest(
      `/SignedCertificates/by-application/${applicationId}`,
      {
        method: "GET",
      },
    );
    const data = await handleResponse<SignedCertificate[]>(response);
    // API returns an array, get the first item
    return Array.isArray(data) && data.length > 0 ? data[0] : null;
  },

  /**
   * Get certificate signatures by signed certificate ID
   */
  getCertificateSignatures: async (
    signedCertificateId: number,
  ): Promise<CertificateSignature[]> => {
    const response = await apiRequest(
      `/CertificateSignatures/by-signed-certificate/${signedCertificateId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<CertificateSignature[]>(response);
  },

  /**
   * Get certificate signatures by certificate application ID
   */
  getCertificateSignaturesByApplicationId: async (
    certificateApplicationId: number,
  ): Promise<CertificateSignature[]> => {
    const response = await apiRequest(
      `/CertificateSignatures/by-certificate-application/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<CertificateSignature[]>(response);
  },

  /**
   * Get authority signatures by certificate application ID
   */
  getAuthoritySignatures: async (
    certificateApplicationId: number,
  ): Promise<AuthoritySignature[]> => {
    const response = await apiRequest(
      `/CertificateApplications/authority-signatures/${certificateApplicationId}`,
      {
        method: "GET",
      },
    );
    return handleResponse<AuthoritySignature[]>(response);
  },
};
